import { useEffect } from 'react';
import gql from 'graphql-tag';
import { useSubscription } from '@apollo/react-hooks';

import { useSubscriptionValues, getAGGClient } from '@confluence/graphql';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient, setContentErrorAttributes } from '@confluence/monitoring';
import { Attribution, getStatusCode } from '@confluence/error-boundary';
import { useSessionData, AccessStatus } from '@confluence/session-data';

export const onContentTypesTitleSubscriptionData = (
	response: any,
	onDataReceived: (contentState: any) => void,
) => {
	const payload = response?.subscriptionData?.data?.confluence_onContentModified;
	const _deltas = payload?._deltas;
	if (!payload || !Array.isArray(_deltas)) {
		return;
	}
	if (_deltas?.length === 0 || _deltas.indexOf('contentTitleUpdated') === -1) {
		return;
	}
	const { contentTitleUpdated } = payload;

	const title = contentTitleUpdated?.contentTitle;
	onDataReceived(title);
};

export const ContentTypesTitleSubscription = ({
	contentType,
	contentId,
	onDataReceived,
	skip = false,
}: {
	contentType: 'page' | 'blogpost';
	contentId: string;
	onDataReceived: (title: any) => void;
	skip?: boolean;
}) => {
	const { id, onSubscriptionComplete, extensions, onSubscriptionData } = useSubscriptionValues({
		contentType,
		contentId,
	});
	const { accessStatus } = useSessionData();
	const skipSubscriptionForAnonUsers = accessStatus === AccessStatus.ANONYMOUS_ACCESS;

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { error } = useSubscription(
		gql`
			subscription ContentTypesTitleSubscription($id: ID!) {
				confluence_onContentModified(id: $id) {
					type
					id
					__typename
					_deltas
					contentTitleUpdated {
						contentTitle
						__typename
					}
				}
			}
		`,
		{
			variables: {
				id,
				__extensions: extensions,
			},
			onSubscriptionComplete,
			onSubscriptionData(response) {
				onContentTypesTitleSubscriptionData(response, onDataReceived);
				onSubscriptionData(response);
			},
			skip: skip || !id || skipSubscriptionForAnonUsers,
			client: getAGGClient(),
		},
	);

	useEffect(() => {
		if (error) {
			const errorMessage =
				error?.message ||
				(error as any)?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.originalError?.message ||
				error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message ||
				error?.graphQLErrors?.[0]?.extensions?.message;

			const statusCode =
				getStatusCode(error) || (error as any)?.errors?.[0]?.extensions?.statusCode;
			if (statusCode !== 403) {
				const e = new Error(`Subscription Error - ContentTypesTitleSubscription:${errorMessage}`);
				setContentErrorAttributes(e, {
					objectId: contentId,
					objectType: contentType,
				});
				getMonitoringClient().submitError(e, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}
	}, [error, contentId, contentType]);

	return null;
};
